import UseCaseTemplate, { type UseCaseContent } from '../../components/UseCaseTemplate';

const content: UseCaseContent = {
  slug: 'ev-parking-india',
  title: "The EV Parking Gap in India — Where the Chargers Aren't | Parksy",
  description:
    "India has 8,000+ public EV chargers but most are in dedicated stations, not parking spots. We map the gap and explain what EV owners actually need.",
  canonicalPath: '/ev-parking-india',
  ogImage: 'https://parksy.rent/og/ev-parking-india.png',
  heroIntro:
    "India's EV transition is happening faster than parking infrastructure can keep up. As of mid-2024, India has 8,000+ public charging stations, but most are in standalone locations — not paired with a parking spot. The result: ICE-ing (combustion cars blocking EV bays), broken chargers, and EV owners circling blocks looking for both a plug and a place to leave the car. Parksy's public-good mission: make every charger findable in one map, with parking attached.",
  painPoints: [
    'India has 8,000+ public EV chargers, but <2% are paired with a parking spot listing on any map.',
    'ICE-ing is the #1 complaint from EV owners in metros.',
    'Apartment EV owners have nowhere to charge overnight in 90% of complexes.',
    'Highway rest stops mix fast chargers with no designated parking bays, leading to unsafe roadside charging.',
    'Charger reliability is undocumented; no crowdsourced status board exists.',
  ],
  solutions: [
    { title: 'Map chargers + parking', body: "On Parksy, you can filter parking spots by 'EV charging' — every match has a charger confirmed by the spot owner. No more guessing." },
    { title: 'Crowdsource reliability', body: 'Rate charging spots on speed, reliability, and safety. Aggregated reports help other EV owners avoid broken chargers.' },
    { title: 'List your charger', body: 'If you have a private charger in a driveway, garage, or commercial lot, list it. We\'ll match you with EV owners in your area.' },
  ],
  citySpotlights: [
    { slug: 'bangalore', name: 'Bangalore', note: 'EV adoption leader' },
    { slug: 'delhi', name: 'Delhi', note: 'FAME-II subsidy hub' },
    { slug: 'mumbai', name: 'Mumbai', note: 'High-rise retrofit' },
    { slug: 'hyderabad', name: 'Hyderabad', note: 'IT corridor EVs' },
    { slug: 'pune', name: 'Pune', note: '2-wheeler EV hotspot' },
    { slug: 'chennai', name: 'Chennai', note: 'Auto industry base' },
  ],
  faqs: [
    { question: 'How many public EV chargers does India have?', answer: '~8,000–10,000 as of mid-2024 (Ministry of Heavy Industries public dashboard). Most are in 4 metros: Delhi, Mumbai, Bangalore, Hyderabad.' },
    { question: 'Why is the EV parking gap a problem?', answer: "Owners can't charge because parking spots don't have chargers, and charger locations don't have parking. Solving one without the other leaves EV owners with no good option." },
    { question: 'Does Parksy list every public charger?', answer: "Not yet. We list parking spots that include EV charging. For a comprehensive public charger map, see the Ministry of Heavy Industries dashboard (heavyindustries.gov.in)." },
    { question: 'Can I list a home charger for public use?', answer: 'Yes. As long as the spot is a parking spot (driveway, garage, lot) with confirmed EV charging, you can list it. Owners set the rate and access terms.' },
  ],
  primaryCta: { label: 'Find EV parking on the map →', to: '/' },
  secondaryCta: { label: 'EV parking use case', to: '/ev-charging-parking' },
  breadcrumbs: [
    { label: 'Home', to: '/' },
    { label: 'EV parking gap' },
  ],
};

export default function EVParkingIndia() {
  return <UseCaseTemplate content={content} />;
}
